import { useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const SIZES = [100, 150, 200, 250];
const COMPANY_LABELS = {
  musicmagpie: 'MusicMagpie',
  webuybooks: 'WeBuyBooks',
  worldofbooks: 'World of Books',
  cex: 'CEX',
};

const PRESETS = [
  { key: 'all', label: 'All 4 companies (max earnings)', companies: ['musicmagpie', 'webuybooks', 'worldofbooks', 'cex'] },
  { key: 'cex', label: 'CEX only (2× rate, no sacrifice)', companies: ['cex'] },
  { key: 'mm', label: 'MusicMagpie only', companies: ['musicmagpie'] },
  { key: 'wbb', label: 'WeBuyBooks only', companies: ['webuybooks'] },
  { key: 'wob', label: 'World of Books only', companies: ['worldofbooks'] },
  { key: 'books', label: 'Book buyers (MM + WBB + WoB)', companies: ['musicmagpie', 'webuybooks', 'worldofbooks'] },
];

export default function BatchSetup({ onStarted }) {
  const [size, setSize] = useState(100);
  const [preset, setPreset] = useState('all');
  const [companies, setCompanies] = useState(['musicmagpie', 'webuybooks', 'worldofbooks', 'cex']);
  const [custom, setCustom] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const pickPreset = (key) => {
    setPreset(key);
    setCustom(false);
    const p = PRESETS.find((x) => x.key === key);
    if (p) setCompanies(p.companies);
  };

  const toggleCompany = (c) => {
    setCustom(true);
    setPreset('');
    setCompanies((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c].sort()
    );
  };

  const pointsPerItem = companies.reduce((n, c) => n + (c === 'cex' ? 2 : 1), 0);
  const maxEarnings = (size * pointsPerItem * 0.01).toFixed(2);

  const start = async () => {
    if (companies.length === 0) {
      setErr('Pick at least one company.');
      return;
    }
    setErr('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/batches/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ size, companies }),
      });
      const data = await res.json();
      if (!res.ok) setErr(data.detail || 'Could not start batch.');
      else onStarted(data);
    } catch {
      setErr('Network error — try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="batch-setup">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-5">
        <h3 className="text-base font-bold text-white mb-2">Batch size</h3>
        <p className="text-xs text-zinc-500 mb-3">
          More barcodes = more earnings. You have 4 hours per batch to complete once started.
        </p>
        <div className="grid grid-cols-4 gap-2">
          {SIZES.map((s) => (
            <button
              key={s}
              onClick={() => setSize(s)}
              className={`py-3 rounded-lg font-bold text-sm border ${
                size === s
                  ? 'bg-[#00D26A] text-black border-[#00D26A]'
                  : 'bg-black border-zinc-700 text-zinc-300 hover:border-zinc-500'
              }`}
              data-testid={`batch-size-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-5">
        <h3 className="text-base font-bold text-white mb-2">Which companies?</h3>
        <p className="text-xs text-zinc-500 mb-3">
          Each company gives 1 data point per barcode. <strong className="text-[#FFD700]">CEX pays 2×</strong> (cash + voucher = 2 points).
        </p>
        <div className="space-y-2 mb-4">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => pickPreset(p.key)}
              className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm ${
                preset === p.key && !custom
                  ? 'border-[#00D26A] bg-[#00D26A]/10'
                  : 'border-zinc-800 bg-black hover:border-zinc-600'
              }`}
              data-testid={`batch-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <details className="mb-1">
          <summary className="cursor-pointer text-xs font-bold text-zinc-400 hover:text-white">
            Or choose individually…
          </summary>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {Object.entries(COMPANY_LABELS).map(([c, label]) => (
              <label
                key={c}
                className="flex items-center gap-2 p-2 rounded-lg bg-black border border-zinc-800"
              >
                <input
                  type="checkbox"
                  checked={companies.includes(c)}
                  onChange={() => toggleCompany(c)}
                  data-testid={`company-toggle-${c}`}
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </details>
      </div>

      <div className="bg-[#FFD700]/10 border border-[#FFD700]/40 rounded-xl p-4 mb-5">
        <p className="text-sm text-zinc-200">
          <strong className="text-[#FFD700]">Max earnings:</strong>{' '}
          <span className="text-xl font-extrabold text-white">£{maxEarnings}</span>
          <span className="text-xs text-zinc-400 ml-2">
            ({size} items × {pointsPerItem} data points × 1p each)
          </span>
        </p>
      </div>

      {err && (
        <p className="text-sm mb-3" style={{ color: '#E04A3F' }} data-testid="setup-error">
          {err}
        </p>
      )}

      <button
        onClick={start}
        disabled={loading}
        className="w-full py-3 rounded-full font-bold disabled:opacity-50"
        style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
        data-testid="start-batch-btn"
      >
        {loading ? 'Starting…' : `Start batch · earn up to £${maxEarnings}`}
      </button>
    </div>
  );
}
